import { useEffect, useState, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { 
  ShieldCheck, 
  UserCircle, 
  Envelope, 
  Globe,
  Lock,
  CheckCircle,
  ArrowRight,
  List,
  X,
  MapPin,
  Buildings,
  ChartLineUp,
  Scales,
  Handshake,
  MagnifyingGlass,
  Blueprint,
  Plugs,
  Rocket,
  Repeat,
  TreeStructure,
  CurrencyCircleDollar,
  GlobeHemisphereWest,
  ShieldCheckered
} from "@phosphor-icons/react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URLs
const LOGO_MAIN = "https://customer-assets.emergentagent.com/job_fintech-sales/artifacts/763igyt8_PB%20Teal%20%26%20Black%20Long.webp";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_695e641f-da76-4a1c-affa-fb110c591801/artifacts/5bce65bi_PB%20Logo.png";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

// Header Component
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#capabilities", label: "Capabilities" },
    { href: "#approach", label: "Our Approach" },
    { href: "#why-us", label: "Why Us" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-2" : "py-3 bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="container-custom flex items-center justify-between">
        <a href="#" className="flex items-center" data-testid="logo-link">
          <img src={LOGO_MAIN} alt="Payment Boutique" className="h-20 md:h-24 w-auto -my-4" />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-slate-700 font-medium hover:text-[#6AC5B8] transition-colors underline-animated"
              data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
            >
              {link.label}
            </a>
          ))}
        </nav>

        <a href="#contact" className="hidden md:block">
          <Button 
            className="bg-[#6AC5B8] hover:bg-[#4BA99C] text-white rounded-full px-6 btn-shine"
            data-testid="header-cta-btn"
          >
            Schedule Consultation
          </Button>
        </a>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass mt-2 mx-4 rounded-xl overflow-hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col p-4 gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-slate-700 font-medium py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-[#6AC5B8] hover:bg-[#4BA99C] text-white rounded-full">
                  Schedule Consultation
                </Button>
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

// Hero Section - Repositioned
const Hero = () => {
  return (
    <section className="min-h-screen gradient-bg flex items-center pt-28 pb-20" data-testid="hero-section">
      <div className="container-custom">
        <div className="max-w-4xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 leading-tight mb-6"
            >
              Bespoke Payment Architecture,{" "}
              <span className="text-[#6AC5B8]">Designed for Precision.</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg lg:text-xl text-slate-600 mb-10 max-w-2xl leading-relaxed"
            >
              We design and manage customized payment infrastructure for established 
              and growing businesses. Through carefully structured onboarding and 
              trusted acquiring relationships, we build payment systems aligned with 
              your long-term stability and operational goals.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <a href="#contact">
                <Button 
                  size="lg" 
                  className="bg-[#6AC5B8] hover:bg-[#4BA99C] text-white rounded-full px-8 btn-shine"
                  data-testid="hero-cta-btn"
                >
                  Schedule Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </a>
              <a href="#approach">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="rounded-full px-8 border-slate-300 hover:border-[#6AC5B8] hover:text-[#6AC5B8]"
                  data-testid="hero-secondary-btn"
                >
                  Our Approach
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Curated Capabilities Section (formerly Services)
const Capabilities = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const capabilities = [
    {
      icon: TreeStructure,
      title: "Payment Architecture & Acquiring",
      description: "Strategic design of your payment infrastructure—matching your business model with aligned acquiring partners for sustainable, long-term processing relationships."
    },
    {
      icon: GlobeHemisphereWest,
      title: "Cross-Border & Multi-Entity Structuring",
      description: "Tailored solutions for businesses operating across jurisdictions. We architect payment flows that accommodate complex corporate structures and international commerce."
    },
    {
      icon: CurrencyCircleDollar,
      title: "Settlement & Cash Flow Optimization",
      description: "Structured settlement strategies designed around your operational cash flow requirements, with precision timing and predictable funding cycles."
    },
    {
      icon: ShieldCheckered,
      title: "Fraud & Compliance Strategy",
      description: "Compliance-forward frameworks aligned with regulated industries. Risk management protocols designed to protect your business and maintain acquiring relationships."
    }
  ];

  return (
    <section id="capabilities" className="py-24 bg-white noise-overlay" data-testid="capabilities-section">
      <div className="container-custom relative z-10" ref={ref}>
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.div variants={fadeInUp} className="badge-teal mb-4">
            Curated Capabilities
          </motion.div>
          <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 mb-4">
            Strategic Payment Solutions
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-slate-600 max-w-2xl mx-auto">
            We don't offer commoditized services. Each engagement is structured around 
            your specific business requirements, growth trajectory, and operational structure.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8"
        >
          {capabilities.map((capability, index) => (
            <motion.div
              key={capability.title}
              variants={fadeInUp}
              className="glass rounded-2xl p-8 card-hover"
              data-testid={`capability-card-${index}`}
            >
              <div className="w-14 h-14 rounded-2xl bg-[#6AC5B8]/10 flex items-center justify-center mb-5">
                <capability.icon size={28} className="text-[#6AC5B8]" weight="duotone" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">{capability.title}</h3>
              <p className="text-slate-600 leading-relaxed">{capability.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Our Approach Section - 5-Step Process
const Approach = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = [
    {
      number: "01",
      title: "Discovery and Review",
      description: "Comprehensive analysis of your business model, processing history, volume projections, and operational requirements."
    },
    {
      number: "02",
      title: "Infrastructure Design",
      description: "Custom payment architecture designed around your specific needs—acquiring strategy, settlement structure, and compliance framework."
    },
    {
      number: "03",
      title: "Acquirer Placement",
      description: "Strategic placement with acquiring partners aligned to your business profile, supporting sustainable, long-term acquiring relationships."
    },
    {
      number: "04",
      title: "Integration & Launch",
      description: "Structured onboarding with dedicated support through technical integration, testing, and production deployment."
    },
    {
      number: "05",
      title: "Ongoing Optimization",
      description: "Continuous monitoring, performance analysis, and strategic adjustments to maintain optimal processing infrastructure."
    }
  ];

  return (
    <section id="approach" className="py-24 bg-[#6AC5B8]/5" data-testid="approach-section">
      <div className="container-custom" ref={ref}>
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.div variants={fadeInUp} className="badge-teal mb-4">
            Our Approach
          </motion.div>
          <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 mb-4">
            A Structured Path to Payment Stability
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-slate-600 max-w-2xl mx-auto">
            Every engagement follows our proven methodology—designed to deliver 
            precision, alignment, and long-term operational success.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="relative"
        >
          {/* Connection line for desktop */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-[#6AC5B8]/20 -translate-y-1/2" />
          
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                variants={fadeInUp}
                className="relative"
                data-testid={`approach-step-${index}`}
              >
                <div className="glass rounded-2xl p-6 text-center card-hover h-full">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[#6AC5B8] text-white flex items-center justify-center text-lg font-semibold mb-4 relative z-10">
                    {step.number}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Why Payment Boutique Section - Authority Positioning
const WhyUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const differentiators = [
    {
      icon: MagnifyingGlass,
      title: "Structured Discovery Process",
      description: "We begin every engagement with a comprehensive review of your business—understanding your model before recommending solutions."
    },
    {
      icon: Handshake,
      title: "Direct Acquiring Relationships",
      description: "Strategic partnerships with acquiring institutions allow us to place merchants where they're aligned for long-term success."
    },
    {
      icon: Buildings,
      title: "Customized Merchant Placement",
      description: "No templated solutions. Your payment infrastructure is designed around your specific industry, jurisdiction, and growth plan."
    },
    {
      icon: UserCircle,
      title: "Dedicated Payments Strategist",
      description: "A single point of contact who understands your business and advocates for your processing needs throughout the relationship."
    },
    {
      icon: ChartLineUp,
      title: "Ongoing Optimization",
      description: "Continuous performance monitoring and strategic adjustments ensure your payment infrastructure evolves with your business."
    },
    {
      icon: Scales,
      title: "Compliance-Forward Operations",
      description: "Structured oversight and risk management protocols designed for regulated industries and complex operational requirements."
    }
  ];

  return (
    <section id="why-us" className="py-24 bg-white" data-testid="why-us-section">
      <div className="container-custom" ref={ref}>
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.div variants={fadeInUp} className="badge-teal mb-4">
            Why Payment Boutique
          </motion.div>
          <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 mb-4">
            A Different Kind of Payments Partner
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-slate-600 max-w-2xl mx-auto">
            We are a curated, relationship-driven payments advisory firm—not a 
            commoditized processing provider. Our approach is built on precision, alignment, and institutional credibility.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {differentiators.map((item, index) => (
            <motion.div 
              key={item.title} 
              variants={fadeInUp}
              className="glass rounded-2xl p-6 card-hover"
              data-testid={`differentiator-${index}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#6AC5B8]/10 flex items-center justify-center mb-4">
                <item.icon size={24} className="text-[#6AC5B8]" weight="duotone" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Compliance & Oversight Section (repositioned Security)
const Compliance = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="compliance" className="py-24 bg-[#6AC5B8]/5" data-testid="compliance-section">
      <div className="container-custom" ref={ref}>
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid lg:grid-cols-2 gap-16 items-start"
        >
          <div>
            <motion.div variants={fadeInUp} className="badge-teal mb-4">
              Compliance & Oversight
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 mb-6">
              Structured for Stability
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-slate-600 mb-8 leading-relaxed">
              Payment Boutique operates with a compliance-forward framework designed to support 
              long-term payment stability and institutional alignment. Our approach emphasizes 
              operational oversight, proactive risk management, acquiring partner alignment, 
              and awareness across multiple jurisdictions—building payment infrastructure 
              that supports sustainable growth.
            </motion.p>
            
            <motion.div variants={staggerContainer} className="space-y-4">
              {[
                "PCI-aligned infrastructure supporting secure transaction environments",
                "Ongoing risk monitoring designed to support account longevity",
                "Payment structures aligned with Canadian, US, and international frameworks",
                "Proactive compliance management and documentation readiness",
                "Third-party audit preparedness",
                "Ongoing relationship management with acquiring partners"
              ].map((item, index) => (
                <motion.div 
                  key={index}
                  variants={fadeInUp}
                  className="flex items-start gap-3"
                >
                  <CheckCircle size={20} className="text-[#6AC5B8] mt-0.5 shrink-0" weight="fill" />
                  <span className="text-slate-700">{item}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <motion.div
            variants={fadeInUp}
            className="glass rounded-2xl p-8"
          >
            <h3 className="text-xl font-semibold text-slate-900 mb-6">Our Compliance Framework</h3>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#6AC5B8]/10 flex items-center justify-center shrink-0">
                  <ShieldCheck size={24} className="text-[#6AC5B8]" weight="duotone" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Secure Processing Framework</h4>
                  <p className="text-slate-600 text-sm">End-to-end encryption, tokenization, and secure data handling aligned with institutional standards.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#6AC5B8]/10 flex items-center justify-center shrink-0">
                  <Scales size={24} className="text-[#6AC5B8]" weight="duotone" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Cross-Border Compliance Alignment</h4>
                  <p className="text-slate-600 text-sm">Structured payment frameworks designed to operate across multiple jurisdictions.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#6AC5B8]/10 flex items-center justify-center shrink-0">
                  <ChartLineUp size={24} className="text-[#6AC5B8]" weight="duotone" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Continuous Risk Oversight</h4>
                  <p className="text-slate-600 text-sm">Ongoing monitoring supported by fraud prevention strategies and structured response planning.</p>
                </div>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-slate-800 font-semibold">
                Our goal is not simply approval, but sustainable payment operations built for long-term growth.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section - Consultative Positioning
const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    business_name: "",
    email: "",
    message: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Inquiry received.", {
        description: "A member of our team will review your submission and respond within one business day."
      });
      setFormData({ name: "", business_name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Submission failed", {
        description: "Please try again or contact us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white noise-overlay" data-testid="contact-section">
      <div className="container-custom relative z-10" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="badge-teal mb-4">
              Begin the Conversation
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 mb-6">
              Is Payment Boutique Right for Your Business?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-slate-600 mb-8 leading-relaxed">
              We partner with established businesses seeking structured payment infrastructure—
              not transactional solutions or short-term fixes. If you're looking for a strategic 
              payments partner aligned with long-term growth, we welcome the conversation.
            </motion.p>

            <motion.div variants={staggerContainer} className="space-y-6">
              <motion.div variants={fadeInUp} className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#6AC5B8]/10 flex items-center justify-center">
                  <Envelope size={24} className="text-[#6AC5B8]" weight="duotone" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium text-slate-900">support@paymentboutique.net</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <form onSubmit={handleSubmit} className="glass rounded-3xl p-8" data-testid="contact-form">
              <h3 className="text-xl font-semibold text-slate-900 mb-6">Request a Discovery Consultation</h3>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Your Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Full name"
                    className="rounded-xl border-slate-200"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Business Name *
                  </label>
                  <Input
                    type="text"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleChange}
                    required
                    placeholder="Company or entity name"
                    className="rounded-xl border-slate-200"
                    data-testid="contact-business-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Business Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="you@company.com"
                    className="rounded-xl border-slate-200"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Tell us about your business and payment requirements *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Industry, current processing volume, specific challenges or requirements..."
                    rows={5}
                    className="rounded-xl border-slate-200 resize-none"
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#6AC5B8] hover:bg-[#4BA99C] text-white rounded-full py-6 btn-shine"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? "Submitting..." : "Submit Inquiry"}
                  {!isSubmitting && <ArrowRight className="ml-2" size={20} />}
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  All inquiries are reviewed by our team. We respond within one business day.
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white py-16" data-testid="footer">
      <div className="container-custom">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <p className="text-slate-400 max-w-md leading-relaxed mb-6">
              A boutique payment infrastructure partner designing bespoke solutions 
              for established businesses. Strategic, structured, relationship-driven.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <Envelope size={20} className="text-[#6AC5B8]" weight="duotone" />
              <span className="text-slate-300">support@paymentboutique.net</span>
            </div>
            <p className="text-slate-500 text-sm">
              Supporting merchants across North America and international markets.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Navigation</h4>
            <ul className="space-y-2 text-slate-400">
              <li><a href="#capabilities" className="hover:text-[#6AC5B8] transition-colors">Capabilities</a></li>
              <li><a href="#approach" className="hover:text-[#6AC5B8] transition-colors">Our Approach</a></li>
              <li><a href="#why-us" className="hover:text-[#6AC5B8] transition-colors">Why Us</a></li>
              <li><a href="#contact" className="hover:text-[#6AC5B8] transition-colors">Contact</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Capabilities</h4>
            <ul className="space-y-2 text-slate-400">
              <li>Payment Architecture</li>
              <li>Cross-Border Structuring</li>
              <li>Settlement Optimization</li>
              <li>Compliance Strategy</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Payment Boutique. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <Header />
      <main>
        <Hero />
        <Capabilities />
        <Approach />
        <WhyUs />
        <Compliance />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
